import {
  Entretien,
  EntretienApprenantResponse,
  EntretienMode,
  EntretienStatut,
} from '../../../domain/models';

const FR_MONTHS = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
];

const FR_DAYS = [
  'dimanche',
  'lundi',
  'mardi',
  'mercredi',
  'jeudi',
  'vendredi',
  'samedi',
];

const FR_DAYS_SHORT = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'];

export function reponseStatus(e: Entretien): EntretienApprenantResponse {
  return e.reponse?.response ?? 'en_attente';
}

export const REPONSE_LABELS: Record<EntretienApprenantResponse, string> = {
  en_attente: 'En attente de réponse',
  confirmed: 'Accepté',
  declined: 'Refusé',
  postponed: 'Report demandé',
};

export const REPONSE_BADGE: Record<EntretienApprenantResponse, string> = {
  en_attente:
    'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-300',
  confirmed:
    'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300',
  declined: 'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-300',
  postponed:
    'bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-300',
};

export const STATUT_LABELS: Record<EntretienStatut, string> = {
  'Entretien fixé': 'Entretien fixé',
  'Entretien confirmé': 'Entretien confirmé',
  'Report demandé': 'Report demandé',
  'Entretien reporté': 'Entretien reporté',
  'Entretien annulé': 'Entretien annulé',
  'Entretien réalisé': 'Entretien réalisé',
};

export const STATUT_BADGE: Record<EntretienStatut, string> = {
  'Entretien fixé':
    'bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-300',
  'Entretien confirmé':
    'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300',
  'Report demandé':
    'bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-300',
  'Entretien reporté':
    'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-300',
  'Entretien annulé': 'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-300',
  'Entretien réalisé':
    'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
};

export const ENTRETIEN_STATUTS: EntretienStatut[] = [
  'Entretien fixé',
  'Entretien confirmé',
  'Report demandé',
  'Entretien reporté',
  'Entretien annulé',
  'Entretien réalisé',
];

interface ModaliteMeta {
  label: string;
  icon: string;
  badge: string;
  dot: string;
}

export const MODALITES: Record<EntretienMode, ModaliteMeta> = {
  Visio: {
    label: 'Visio',
    icon: '📹',
    badge: 'bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-300',
    dot: 'bg-blue-500',
  },
  Présentiel: {
    label: 'Présentiel',
    icon: '📍',
    badge:
      'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300',
    dot: 'bg-emerald-500',
  },
  Téléphone: {
    label: 'Téléphone',
    icon: '📞',
    badge:
      'bg-orange-50 text-orange-600 dark:bg-orange-500/10 dark:text-orange-300',
    dot: 'bg-orange-500',
  },
};

export function apprenantFullName(e: Entretien): string {
  if (!e.apprenant) return '—';
  return `${e.apprenant.prenom} ${e.apprenant.nom}`.trim() || '—';
}

export function conseillerFullName(e: Entretien): string {
  if (!e.conseiller) return '—';
  return `${e.conseiller.prenom} ${e.conseiller.nom}`.trim() || '—';
}

export function scheduledAt(e: Entretien): string {
  if (!e.date) return '';
  const time = e.heure?.trim() || '00:00';
  return `${e.date}T${time}:00`;
}

/** "sam. 20 juin" */
export function shortDate(dateStr: string): string {
  const d = new Date(dateStr.includes('T') ? dateStr : `${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) {
    return dateStr;
  }
  return `${FR_DAYS_SHORT[d.getDay()]} ${d.getDate()} ${FR_MONTHS[d.getMonth()]}`;
}

/** "samedi 20 juin 2026" */
export function longDate(dateStr: string): string {
  const d = new Date(dateStr.includes('T') ? dateStr : `${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) {
    return dateStr;
  }
  return `${FR_DAYS[d.getDay()]} ${d.getDate()} ${FR_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** "12 juin 2026" */
export function plainDate(dateStr: string): string {
  const d = new Date(dateStr.includes('T') ? dateStr : `${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) {
    return dateStr;
  }
  return `${d.getDate()} ${FR_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** "09:00" from entretien or ISO string */
export function timeLabel(value: string): string {
  if (/^\d{2}:\d{2}$/.test(value)) {
    return value;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return value;
  }
  const hh = `${d.getHours()}`.padStart(2, '0');
  const mm = `${d.getMinutes()}`.padStart(2, '0');
  return `${hh}:${mm}`;
}

export function monthLabel(year: number, month: number): string {
  return `${FR_MONTHS[month][0].toUpperCase()}${FR_MONTHS[month].slice(1)} ${year}`;
}

/** Local YYYY-MM-DD key (avoids UTC shift from toISOString). */
export function dayKey(date: Date): string {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, '0');
  const d = `${date.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function avatarInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
}

export function avatarColor(seed: string): string {
  const colors = ['#4F46E5', '#0891B2', '#059669', '#D97706', '#DC2626', '#7C3AED'];
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}
